import { isExactVoucherTypeRow } from "../../../services/billingImportFindService";
import {
  lineAmountOnly,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
} from "./salesLineChunksReader";
import {
  normalizeSalesImportBranchId,
  outwardSupplyVoucherTypeForBranch,
  salesImportBranchDisplayName,
  salesImportBranchOutwardSupplyLabel,
} from "../config/salesImportBranchIds";
import {
  importRowEmployeeName,
  importRowSalespersonName,
  importRowStaffName,
} from "../utils/importRowFields";
import { parseTranDateLoose, rowTranDateValue, toYmd } from "../utils/dateParse";

const PLACEHOLDER_EMPLOYEE = "an employee";

/** Discount % bands for discounted-line drill-down. */
export const DISCOUNT_PCT_BUCKETS = [
  { label: "0 – 5%", maxExclusive: 6 },
  { label: "6 – 15%", min: 6, max: 15 },
  { label: "16 – 25%", min: 16, max: 25 },
  { label: "26 – 35%", min: 26, max: 35 },
  { label: "36 – 45%", min: 36, max: 45 },
  { label: "46 – 55%", min: 46, max: 55 },
  { label: "56 – 65%", min: 56, max: 65 },
  { label: "66 – 75%", min: 66, max: 75 },
  { label: "76 – 85%", min: 76, max: 85 },
  { label: "86 – 95%", min: 86, max: 95 },
  { label: "96 – 100%", min: 96, max: 100 },
];

export function discountPctBucketLabel(pct) {
  const p = Math.round(Math.min(100, Math.max(0, Number(pct) || 0)) * 10) / 10;

  if (p < 6) return "0 – 5%";
  if (p <= 15) return "6 – 15%";
  if (p <= 25) return "16 – 25%";
  if (p <= 35) return "26 – 35%";
  if (p <= 45) return "36 – 45%";
  if (p <= 55) return "46 – 55%";
  if (p <= 65) return "56 – 65%";
  if (p <= 75) return "66 – 75%";
  if (p <= 85) return "76 – 85%";
  if (p <= 95) return "86 – 95%";
  return "96 – 100%";
}

/** Line discount % = discount amount ÷ (net + discount). Rounded to 1 decimal for display and buckets. */
export function lineDiscountPct(line) {
  const disc = Number(line.discountAmt) || 0;
  if (disc <= 0.001) return 0;

  const net = Number(line.netAmount) || 0;
  const gross = net + disc;
  if (gross > 0.001) {
    const raw = Math.min(100, Math.max(0, (disc / gross) * 100));
    return Math.round(raw * 10) / 10;
  }

  const linePct = Number(line.maxDiscountPct) || 0;
  if (linePct > 0.001 && linePct <= 100) return Math.round(linePct * 10) / 10;

  return 0;
}

/** @deprecated Use lineDiscountPct — kept for export/tests compatibility. */
export function invoiceDiscountPct(line) {
  return lineDiscountPct(line);
}

function lineRawDiscountAmt(row) {
  return Math.max(0, Number(row.discountAmt ?? row.discountAm) || 0);
}

function isHeaderDiscountRepeated(lines) {
  if (lines.length <= 1) return false;
  const maxDisc = Math.max(0, ...lines.map((l) => l.rawDiscountAmt));
  if (maxDisc <= 0) return false;
  const sumDisc = lines.reduce((s, l) => s + l.rawDiscountAmt, 0);
  return sumDisc >= maxDisc * lines.length * 0.9;
}

function parseLineFromRow(row, lineIndex) {
  const voucherNo = String(row.voucherNo ?? row.VoucherNo ?? "").trim();
  const netAmount = Math.abs(lineAmountOnly(row));
  const dt = parseTranDateLoose(rowTranDateValue(row));

  return {
    lineKey: `${voucherNo}|${lineIndex}|${String(row.partNo ?? row.productId ?? "").trim()}`,
    invoiceNo: voucherNo,
    invoiceDate: dt ? toYmd(dt) : "",
    tranDateObj: dt,
    branchId: normalizeSalesImportBranchId(row.branchId ?? row.BranchID ?? row.branch ?? ""),
    customerName: String(row.customerName ?? row.CustomerName ?? "").trim(),
    employeeName: importRowEmployeeName(row),
    staffName: importRowStaffName(row),
    salespersonName: importRowSalespersonName(row),
    itemName: String(row.itemName ?? row.ItemName ?? "").trim(),
    partNo: String(row.partNo ?? row.PartNo ?? "").trim(),
    qty: Number(row.qty ?? row.Qty) || 0,
    netAmount,
    rawDiscountAmt: lineRawDiscountAmt(row),
    maxDiscountPct: Number(row.discount) || 0,
    voucherType: normalizeVoucherType(row),
  };
}

function finalizeLineItem(line) {
  line.grossAmount = line.netAmount + line.discountAmt;
  line.discountPct = lineDiscountPct(line);
  line.isDiscounted = (Number(line.discountAmt) || 0) > 0.001;
  line.isNonDiscountSale = !line.isDiscounted && isPlaceholderEmployee(line.employeeName);
  line.isEmployeeSale = !line.isDiscounted && isRealEmployee(line.employeeName);
  line.branchLabel = salesImportBranchDisplayName(line.branchId);
  delete line.rawDiscountAmt;
  delete line.maxDiscountPct;
  return line;
}

/**
 * When bill discount is repeated on every line, split it across lines by net share.
 * Otherwise each line keeps its own discount amount.
 */
function allocateVoucherLineDiscounts(parsedLines) {
  const headerRepeated = isHeaderDiscountRepeated(parsedLines);
  const voucherDiscount = headerRepeated
    ? Math.max(0, ...parsedLines.map((l) => l.rawDiscountAmt))
    : 0;
  const totalNet = parsedLines.reduce((s, l) => s + l.netAmount, 0);

  return parsedLines.map((line) => {
    let discountAmt;
    if (headerRepeated) {
      discountAmt = totalNet > 0 ? (voucherDiscount * line.netAmount) / totalNet : 0;
    } else {
      discountAmt = line.rawDiscountAmt;
    }
    return finalizeLineItem({
      ...line,
      discountAmt: Math.round(Math.max(0, discountAmt) * 100) / 100,
    });
  });
}

/**
 * Aggregate import rows into item-wise Outward Supply lines (one row per item line).
 * @param {object[]} rows
 */
export function aggregateOutwardSupplyLineItems(rows) {
  const byVoucher = new Map();

  for (const row of rows || []) {
    const voucherNo = String(row.voucherNo ?? row.VoucherNo ?? "").trim();
    if (!voucherNo) continue;
    if (!byVoucher.has(voucherNo)) byVoucher.set(voucherNo, []);
    byVoucher.get(voucherNo).push(row);
  }

  const items = [];
  for (const voucherRows of byVoucher.values()) {
    const parsed = voucherRows.map((row, i) => parseLineFromRow(row, i));
    const customer = parsed.find((l) => l.customerName)?.customerName || "";
    const employeeName = parsed.find((l) => l.employeeName)?.employeeName || "";
    const staffName = parsed.find((l) => l.staffName)?.staffName || "";
    for (const line of parsed) {
      if (!line.customerName) line.customerName = customer;
      if (!line.employeeName) line.employeeName = employeeName;
      if (!line.staffName) line.staffName = staffName;
      line.salespersonName = line.staffName || line.employeeName;
    }
    items.push(...allocateVoucherLineDiscounts(parsed));
  }

  return items;
}

export function buildDiscountAmountBreakdown(lineItems) {
  const buckets = DISCOUNT_PCT_BUCKETS.map((b) => ({
    label: b.label,
    lineCount: 0,
    invoiceCount: 0,
    netSales: 0,
    discountAmount: 0,
    items: [],
    invoiceNos: new Set(),
  }));

  for (const line of lineItems || []) {
    if (!line.isDiscounted) continue;
    const pct = lineDiscountPct(line);
    const label = discountPctBucketLabel(pct);
    const bucket = buckets.find((b) => b.label === label);
    if (!bucket) continue;
    bucket.lineCount += 1;
    bucket.invoiceNos.add(line.invoiceNo);
    bucket.netSales += line.netAmount;
    bucket.discountAmount += line.discountAmt;
    bucket.items.push(mapDiscountedLineRow(line, pct, label));
  }

  return buckets
    .filter((b) => b.lineCount > 0)
    .map(({ invoiceNos, items, ...rest }) => ({
      ...rest,
      invoiceCount: invoiceNos.size,
      items,
      /** @deprecated drill-down alias */
      invoices: items,
    }));
}

export function mapDiscountedLineRow(line, pctOverride, bucketOverride) {
  const discountPct = pctOverride != null ? pctOverride : lineDiscountPct(line);
  const discountPctBucket = bucketOverride || discountPctBucketLabel(discountPct);
  return {
    lineKey: line.lineKey,
    invoiceNo: line.invoiceNo,
    invoiceDate: line.invoiceDate,
    itemName: line.itemName || line.partNo || "—",
    partNo: line.partNo,
    qty: line.qty,
    branch: line.branchLabel,
    customerName: line.customerName,
    employee: line.employeeName,
    staffName: line.staffName,
    grossAmount: line.grossAmount,
    discountAmount: line.discountAmt,
    netAmount: line.netAmount,
    discountPct,
    discountPctBucket,
    voucherType: line.voucherType,
  };
}

/** @deprecated Use mapDiscountedLineRow */
export function mapDiscountedInvoiceRow(line, pctOverride, bucketOverride) {
  return mapDiscountedLineRow(line, pctOverride, bucketOverride);
}

export function listDiscountedLineItems(lineItems) {
  return (lineItems || [])
    .filter((line) => line.isDiscounted)
    .map((line) => mapDiscountedLineRow(line))
    .sort((a, b) => {
      if (a.invoiceDate !== b.invoiceDate) return (b.invoiceDate || "").localeCompare(a.invoiceDate || "");
      if (a.invoiceNo !== b.invoiceNo) {
        return a.invoiceNo.localeCompare(b.invoiceNo, undefined, { numeric: true });
      }
      return (a.itemName || "").localeCompare(b.itemName || "");
    });
}

/** @deprecated Use listDiscountedLineItems */
export function listDiscountedInvoices(lineItems) {
  return listDiscountedLineItems(lineItems);
}

export function isPlaceholderEmployee(name) {
  const n = String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
  return n === PLACEHOLDER_EMPLOYEE;
}

export function isRealEmployee(name) {
  const n = String(name || "").trim();
  return Boolean(n) && !isPlaceholderEmployee(n);
}

function calendarMonthRange(year, monthIndex) {
  const from = new Date(year, monthIndex, 1);
  const to = new Date(year, monthIndex + 1, 0);
  return { fromDate: toYmd(from), toDate: toYmd(to) };
}

function monthKeyFromDate(dateObj) {
  if (!dateObj) return "";
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  if (!key) return "—";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
}

function sumLineItems(items, predicate = () => true) {
  let lineCount = 0;
  let amount = 0;
  let discount = 0;
  const invoiceNos = new Set();
  for (const line of items) {
    if (!predicate(line)) continue;
    lineCount += 1;
    amount += line.netAmount;
    discount += line.discountAmt;
    invoiceNos.add(line.invoiceNo);
  }
  return { lineCount, amount, discount, invoiceCount: invoiceNos.size };
}

function buildEmployeeRanking(lineItems, totalEmployeeSales) {
  const map = new Map();
  for (const line of lineItems) {
    if (!line.isEmployeeSale) continue;
    const emp = line.employeeName.trim();
    const prev = map.get(emp) || { employee: emp, amount: 0, lineCount: 0, invoiceNos: new Set() };
    prev.amount += line.netAmount;
    prev.lineCount += 1;
    prev.invoiceNos.add(line.invoiceNo);
    map.set(emp, prev);
  }

  return [...map.values()]
    .sort((a, b) => b.amount - a.amount)
    .map((row, i) => ({
      rank: i + 1,
      employee: row.employee,
      salesAmount: row.amount,
      lineCount: row.lineCount,
      invoiceCount: row.invoiceNos.size,
      averageLine: row.lineCount ? row.amount / row.lineCount : 0,
      contributionPct: totalEmployeeSales > 0 ? (row.amount / totalEmployeeSales) * 100 : 0,
    }));
}

function buildEmployeeBarData(lineItems) {
  const map = new Map();
  for (const line of lineItems) {
    if (!line.isEmployeeSale) continue;
    const emp = line.employeeName.trim();
    map.set(emp, (map.get(emp) || 0) + line.netAmount);
  }
  return [...map.entries()]
    .map(([employee, amount]) => ({ employee, amount }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 15);
}

function collectEmployeeOptions(lineItems) {
  const set = new Set();
  for (const line of lineItems) {
    const n = String(line.employeeName || "").trim();
    if (n) set.add(n);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

function filterByEmployee(lineItems, employeeName) {
  if (!employeeName || employeeName === "all") return lineItems;
  const target = String(employeeName).trim().toLowerCase();
  return lineItems.filter((line) => String(line.employeeName || "").trim().toLowerCase() === target);
}

function buildPieChart(lineItems) {
  const nonDiscount = sumLineItems(lineItems, (line) => line.isNonDiscountSale);
  const discounted = sumLineItems(lineItems, (line) => line.isDiscounted);
  const employee = sumLineItems(lineItems, (line) => line.isEmployeeSale);

  return [
    {
      name: "Non-Discount Sales",
      value: nonDiscount.amount,
      lineCount: nonDiscount.lineCount,
      invoiceCount: nonDiscount.invoiceCount,
      color: "#5d0081",
    },
    {
      name: "Discount Sales",
      value: discounted.amount,
      lineCount: discounted.lineCount,
      invoiceCount: discounted.invoiceCount,
      color: "#9333ea",
    },
    {
      name: "Commission Sales",
      value: employee.amount,
      lineCount: employee.lineCount,
      invoiceCount: employee.invoiceCount,
      color: "#0d9488",
    },
  ];
}

function buildSummaryFooters(lineItems) {
  const total = sumLineItems(lineItems);
  const nonDiscount = sumLineItems(lineItems, (line) => line.isNonDiscountSale);
  const employee = sumLineItems(lineItems, (line) => line.isEmployeeSale);
  const discounted = sumLineItems(lineItems, (line) => line.isDiscounted);

  const discountPcts = lineItems.filter((line) => line.isDiscounted).map((line) => line.discountPct);
  const avgDiscountPct =
    discountPcts.length > 0 ? discountPcts.reduce((s, v) => s + v, 0) / discountPcts.length : 0;

  let highest = null;
  let lowest = null;
  for (const line of lineItems) {
    if (!line.isDiscounted) continue;
    if (!highest || line.discountPct > highest.discountPct) highest = line;
    if (!lowest || line.discountPct < lowest.discountPct) lowest = line;
  }

  return {
    totalOutwardAmount: total.amount,
    totalNonDiscountSales: nonDiscount.amount,
    totalEmployeeSales: employee.amount,
    totalDiscountedSales: discounted.amount,
    totalDiscountGiven: discounted.discount,
    averageDiscountPct: avgDiscountPct,
    highestDiscountLine: highest
      ? {
          invoiceNo: highest.invoiceNo,
          itemName: highest.itemName || highest.partNo || "—",
          discountPct: highest.discountPct,
          amount: highest.netAmount,
        }
      : null,
    lowestDiscountLine: lowest
      ? {
          invoiceNo: lowest.invoiceNo,
          itemName: lowest.itemName || lowest.partNo || "—",
          discountPct: lowest.discountPct,
          amount: lowest.netAmount,
        }
      : null,
    /** @deprecated invoice-level aliases */
    highestDiscountInvoice: highest
      ? { invoiceNo: highest.invoiceNo, discountPct: highest.discountPct, amount: highest.netAmount }
      : null,
    lowestDiscountInvoice: lowest
      ? { invoiceNo: lowest.invoiceNo, discountPct: lowest.discountPct, amount: lowest.netAmount }
      : null,
  };
}

/**
 * @param {{
 *   year: number,
 *   monthIndex: number,
 *   branchId: string,
 *   employeeName?: string,
 *   onProgress?: (rows: number, chunks?: number) => void,
 * }} params
 */
export async function fetchDiscountSalesReport(params) {
  const year = Number(params.year);
  const monthIndex = Number(params.monthIndex);
  const branchId = normalizeSalesImportBranchId(params.branchId);

  if (!branchId) {
    throw new Error("Branch is required.");
  }

  const selectedRange = calendarMonthRange(year, monthIndex);
  const selectedMonthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  const rowFilter = (row) =>
    isExactVoucherTypeRow(row, outwardSupplyVoucherTypeForBranch(branchId));

  const { rows, chunksLoaded } = await loadLineChunkRowsForDateRange(
    selectedRange,
    (rowCount, chunks) => params.onProgress?.(rowCount, chunks),
    { rowFilter }
  );

  const monthLineItems = aggregateOutwardSupplyLineItems(rows).filter(
    (line) => monthKeyFromDate(line.tranDateObj) === selectedMonthKey
  );
  const scopedLineItems = filterByEmployee(monthLineItems, params.employeeName);

  const total = sumLineItems(scopedLineItems);
  const nonDiscount = sumLineItems(scopedLineItems, (line) => line.isNonDiscountSale);
  const employee = sumLineItems(scopedLineItems, (line) => line.isEmployeeSale);
  const discounted = sumLineItems(scopedLineItems, (line) => line.isDiscounted);

  const discountSharePct = total.amount > 0 ? (discounted.amount / total.amount) * 100 : 0;

  const employeeRanking = buildEmployeeRanking(scopedLineItems, employee.amount);
  const employeeBar = buildEmployeeBarData(scopedLineItems);
  const employeeOptions = collectEmployeeOptions(monthLineItems);
  const summary = buildSummaryFooters(scopedLineItems);

  const pieChart = buildPieChart(scopedLineItems);
  const discountBreakdown = buildDiscountAmountBreakdown(scopedLineItems);
  const discountedLineItems = listDiscountedLineItems(scopedLineItems);

  return {
    meta: {
      year,
      monthIndex,
      monthLabel: monthLabel(selectedMonthKey),
      branchId,
      branchLabel: salesImportBranchOutwardSupplyLabel(branchId),
      employeeName: params.employeeName || "all",
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      lineCount: rows.length,
      itemCount: scopedLineItems.length,
      invoiceCount: total.invoiceCount,
      chunksLoaded,
    },
    cards: {
      totalOutwardSales: {
        amount: total.amount,
        lineCount: total.lineCount,
        invoiceCount: total.invoiceCount,
      },
      nonDiscountSales: {
        amount: nonDiscount.amount,
        lineCount: nonDiscount.lineCount,
        invoiceCount: nonDiscount.invoiceCount,
      },
      employeeSales: {
        amount: employee.amount,
        lineCount: employee.lineCount,
        invoiceCount: employee.invoiceCount,
      },
      discountedSales: {
        amount: discounted.amount,
        lineCount: discounted.lineCount,
        invoiceCount: discounted.invoiceCount,
        discountAmount: discounted.discount,
      },
      discountPercentage: discountSharePct,
    },
    summary,
    charts: {
      pie: pieChart,
      employeeBar,
      discountBreakdown,
    },
    employeeRanking,
    employeeOptions,
    discountedLineItems,
    /** @deprecated invoice-level alias */
    discountedInvoices: discountedLineItems,
  };
}
